/**
 * Formateo de lo que ve una persona.
 *
 * Todo lo que se muestra pasa por aquí, y todo recibe el idioma: no hay
 * ningún `toLocaleString()` sin locale explícito repartido por los
 * componentes. Con `es`, `en` y `de` conviviendo, una fecha formateada con el
 * idioma del sistema en lugar del de la empleada es un error que no se ve
 * hasta que lo usa alguien con el móvil en otro idioma.
 */

import type { Idioma } from '../i18n/index.js';

// `de-DE` y no `de`: el locale a secas deja que el navegador elija la variante
// —`de-AT`, `de-CH`— y en Suiza el separador de miles de `Intl.NumberFormat`
// es un apostrofo (`1'024`), asi que el mismo GB libre del pie se escribia
// distinto segun quien abriera el panel. El idioma del panel lo elige la
// oficina; el pais del navegador no tiene por que decidir como se escribe un
// numero nuestro.
const LOCALES: Record<Idioma, string> = { es: 'es-ES', en: 'en-GB', de: 'de-DE' };

/**
 * `3:48` o `12:04`. **Para duraciones de vídeo, y solo para eso.**
 *
 * Los minutos no se desbordan a horas a propósito: un clip por estancia dura
 * menos de un minuto y verlo como `0:48` es lo natural. Para lo que dura una
 * limpieza entera está {@see duracionLarga}, y usar esta en su lugar es lo que
 * ponía `2416:21` en la columna del panel —cuarenta horas, de una limpieza que
 * se quedó abierta— donde debía poner `40 h 16 min`.
 */
export function duracion(ms: number): string {
    const total = Math.max(0, Math.round(ms / 1000));
    const min = Math.floor(total / 60);
    const seg = total % 60;

    return `${min}:${String(seg).padStart(2, '0')}`;
}

/**
 * `1 h 12 min`, `48 min` o `40 s`. Para lo que dura una limpieza entera.
 *
 * Por debajo del minuto se dan segundos: redondear a minutos convertía
 * cuarenta segundos en `0 min`, que parece un dato que falta y no lo es.
 *
 * ### Ya no recibe idioma, y antes lo recibía sin usarlo
 *
 * Aquí había `const h = idioma === 'es' ? 'h' : 'h';` y su gemelo con `min`:
 * un ternario sobre el idioma con **las dos ramas iguales**. O sea que esta
 * función pedía un idioma, lo ignoraba, y parecía que traducía. Lo que lo
 * sostenía es que `noUnusedParameters` daba el parámetro por usado: el
 * ternario muerto era su único consumidor.
 *
 * Y dejarlo así invita al fallo siguiente, que es justo el que se estaba
 * arreglando: quien añada un idioma le pone una tercera rama idéntica y sigue
 * sin traducir nada. `h`, `min` y `s` son símbolos del SI y se escriben igual
 * en español, en inglés y en alemán, así que aquí no hay nada que traducir y
 * ahora se ve.
 */
export function duracionLarga(segundos: number): string {
    const total = Math.max(0, Math.round(segundos));

    if (total < 60) {
        return `${total} s`;
    }

    const horas = Math.floor(total / 3600);
    const min = Math.round((total % 3600) / 60);

    return horas > 0 ? `${horas} h ${min} min` : `${min} min`;
}

/**
 * `184 MB`, `1,2 GB`.
 *
 * Base 1024 pero con las etiquetas de siempre: es lo que pide el handoff y lo
 * que espera cualquiera que mire luego el tamaño en File Station, que también
 * cuenta así.
 */
export function bytes(n: number, idioma: Idioma = 'es'): string {
    const nf = new Intl.NumberFormat(LOCALES[idioma], { maximumFractionDigits: 1 });

    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${nf.format(n / 1024)} kB`;
    if (n < 1024 * 1024 * 1024) return `${nf.format(n / (1024 * 1024))} MB`;
    if (n < 1024 ** 4) return `${nf.format(n / (1024 * 1024 * 1024))} GB`;

    // TB, que hasta ahora no hacía falta.
    //
    // Se paraba en GB porque el DS218 del cliente es un volumen pequeño y
    // ningún número de esta aplicación llegaba ahí. Con la pila en un servidor
    // de 11 TB, el pie del panel decía «3.690,6 GB libres de 11.175,4 GB» en
    // una barra lateral de 236 px fijos: cinco dígitos y un separador de
    // miles para un dato que se lee de un vistazo. Y el alemán, que es el
    // idioma largo, lo tenía peor.
    return `${nf.format(n / 1024 ** 4)} TB`;
}

/**
 * Convierte lo que manda la API en un `Date`.
 *
 * PostgreSQL a través de PHP devuelve `2026-08-27 08:53:00+00`: con un espacio
 * en lugar de la `T` y con la zona en `+00` en vez de `+00:00`. Ese formato
 * **no está en la especificación** de `Date.parse`, así que cada navegador
 * hace lo que quiere: Chrome lo acepta y Safari, históricamente, devuelve
 * `Invalid Date`. Una hora que se ve bien en el escritorio y sale vacía en el
 * iPhone de la empleada es exactamente el fallo que no se detecta hasta que
 * alguien se queja.
 *
 * Se normaliza aquí, en un solo sitio, y todo lo demás usa esta función.
 */
export function aFecha(cuando: string | Date): Date {
    if (cuando instanceof Date) {
        return cuando;
    }

    const conT = cuando.replace(' ', 'T');

    // El arreglo de la zona SOLO si hay hora. Sin esta comprobación, una fecha
    // suelta como '2026-08-27' termina en '-27', que coincide con el patrón de
    // una zona horaria, y se convertía en '2026-08-27:00' = Invalid Date. Lo
    // descubrió el panel enseñando "Invalid Date · 0 registradas" en su
    // cabecera, con la fecha del filtro como entrada.
    const iso = conT.includes('T') ? conT.replace(/([+-]\d{2})$/, '$1:00') : conT;

    return new Date(iso);
}

/**
 * La fecha de HOY según el reloj del dispositivo, en formato `YYYY-MM-DD`.
 *
 * No sirve `new Date().toISOString().slice(0, 10)`, que es la trampa obvia:
 * eso da la fecha en UTC, y a la 01:00 de Madrid en verano son dos horas menos,
 * o sea AYER. El panel pedía entonces el trabajo del día anterior y salía una
 * tabla vacía con la base llena. Es la misma trampa que la de `assignment.fecha`
 * en el esquema, vista desde el navegador.
 */
export function fechaLocalISO(d: Date = new Date()): string {
    const dos = (n: number): string => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/** `09:41`. Hora local del móvil, que es la que ve la empleada en su reloj. */
export function hora(cuando: string | Date, idioma: Idioma = 'es'): string {
    return aFecha(cuando).toLocaleTimeString(LOCALES[idioma], {
        hour: '2-digit',
        minute: '2-digit',
    });
}

/** `26 ago 2026`. */
export function fecha(cuando: string | Date, idioma: Idioma = 'es'): string {
    return aFecha(cuando).toLocaleDateString(LOCALES[idioma], {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

/** `martes, 26 de agosto`: el encabezado de la pantalla 02. */
export function fechaLarga(d: Date, idioma: Idioma = 'es'): string {
    return d.toLocaleDateString(LOCALES[idioma], {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    });
}

/** Iniciales para el avatar de 44 px: `Marta G.` -> `MG`. */
export function iniciales(nombre: string): string {
    const partes = nombre.trim().split(/\s+/).filter(Boolean);
    const primera = partes[0]?.[0] ?? '?';
    const segunda = partes.length > 1 ? partes[partes.length - 1]?.[0] ?? '' : '';

    return (primera + segunda).toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');
}

/** `a3f1c8…` para el sello de la prueba: 8 caracteres bastan para reconocerlo. */
export function shaCorto(sha: string): string {
    return sha.length <= 10 ? sha : `${sha.slice(0, 8)}…`;
}
